import { EllipsisVertical } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useDismissable } from "@/lib/useDismissable";

const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

interface KebabMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

export function KebabMenu({
  items,
  label = "Open menu",
}: {
  items: KebabMenuItem[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pendingFocus, setPendingFocus] = useState<"first" | "last" | null>(
    null,
  );
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const ref = useDismissable<HTMLDivElement>(
    open,
    (reason) => {
      setOpen(false);

      if (reason === "escape") {
        triggerRef.current?.focus();
      }
    },
    menuRef,
  );

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;

    if (!trigger || !menu) {
      return;
    }

    const anchor = trigger.getBoundingClientRect();
    const { offsetHeight: height, offsetWidth: width } = menu;
    const opensUp =
      anchor.bottom + MENU_GAP + height > window.innerHeight &&
      anchor.top - MENU_GAP - height > 0;

    setPosition({
      top: opensUp ? anchor.top - MENU_GAP - height : anchor.bottom + MENU_GAP,
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          anchor.right - width,
          window.innerWidth - width - VIEWPORT_MARGIN,
        ),
      ),
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function close() {
      setOpen(false);
    }

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const enabledIndexes = useMemo(
    () => items.flatMap((item, index) => (item.disabled ? [] : [index])),
    [items],
  );

  const focusFrom = useCallback(
    (currentIndex: number, delta: number) => {
      if (enabledIndexes.length === 0) {
        return;
      }

      const position = enabledIndexes.indexOf(currentIndex);
      const next =
        position === -1
          ? delta > 0
            ? 0
            : enabledIndexes.length - 1
          : (position + delta + enabledIndexes.length) % enabledIndexes.length;

      itemRefs.current[enabledIndexes[next]!]?.focus();
    },
    [enabledIndexes],
  );

  useEffect(() => {
    if (!open || !pendingFocus) {
      return;
    }

    focusFrom(-1, pendingFocus === "first" ? 1 : -1);
    // oxlint-disable-next-line react/set-state-in-effect
    setPendingFocus(null);
  }, [open, pendingFocus, focusFrom]);

  function handleTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
      return;
    }

    e.preventDefault();
    setOpen(true);
    setPendingFocus(e.key === "ArrowDown" ? "first" : "last");
  }

  function handleMenuKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Tab") {
      setOpen(false);

      return;
    }

    const currentIndex = itemRefs.current.findIndex(
      (element) => element === document.activeElement,
    );

    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusFrom(currentIndex, 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusFrom(currentIndex, -1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusFrom(-1, 1);
    } else if (e.key === "End") {
      e.preventDefault();
      focusFrom(-1, -1);
    }
  }

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleTriggerKeyDown}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded-lg p-2 text-foreground hover:bg-muted"
      >
        <EllipsisVertical className="h-5 w-5" fill="currentColor" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            onKeyDown={handleMenuKeyDown}
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? "visible" : "hidden",
            }}
            className="fixed z-50 w-40 rounded-lg border border-muted bg-surface py-1 shadow-2xl"
          >
            {items.map((item, index) => (
              <button
                key={item.label}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                className={`block w-full px-4 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 ${
                  item.danger ? "text-danger" : "text-foreground"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
